import { useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { XClose } from "@untitledui/icons"

import { useSlots } from "@/lib/api/use-appointments"
import { SlotPicker } from "@/components/calendar/slot-picker"
import { cx } from "@/lib/utils/cx"
import { DARK_BTN, pill } from "./shared"

// Book them — the shop's real calendar (the same slots as /calendar), two
// taps: a time, then Book. The decision behind it moves the deal, flips
// the estimate and texts the confirmation.

export function BookDialog({
  open,
  teamId,
  contactName,
  onClose,
  onBook,
}: {
  open: boolean
  teamId: number | string
  contactName: string
  onClose: () => void
  onBook: (startsAt: string, kind: "inspection" | "dropoff") => Promise<void>
}) {
  const reduce = useReducedMotion()
  const from = useMemo(() => new Date().toISOString(), [])
  const to = useMemo(() => new Date(Date.now() + 14 * 86_400_000).toISOString(), [])
  const slots = useSlots(open ? teamId : null, from, to)
  const [picked, setPicked] = useState<string | null>(null)
  const [kind, setKind] = useState<"inspection" | "dropoff">("dropoff")
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (open) { setPicked(null); setBusy(false) } }, [open])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  const book = async () => {
    if (!picked || busy) return
    setBusy(true)
    try { await onBook(picked, kind) } finally { setBusy(false) }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/30 p-0 sm:items-center sm:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={onClose}>
          <motion.div
            role="dialog" aria-modal="true" aria-label={`Book ${contactName}`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(12px) scale(0.98)" }}
            animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(8px) scale(0.98)" }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[88dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl ring-1 ring-zinc-900/10 sm:max-w-[560px] sm:rounded-2xl"
          >
            <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-4">
              <div>
                <h2 className="text-[16px] font-semibold text-zinc-900">Book {contactName}</h2>
                <p className="mt-0.5 text-[12.5px] text-zinc-500">Pick a time on the shop calendar. They get a text to confirm.</p>
              </div>
              <button type="button" onClick={onClose} className="grid size-9 shrink-0 place-items-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30" aria-label="Close"><XClose className="size-4" /></button>
            </div>
            <div className="flex gap-1.5 px-5 pb-3" role="radiogroup" aria-label="Kind of visit">
              <button type="button" role="radio" aria-checked={kind === "dropoff"} onClick={() => setKind("dropoff")} className={pill(kind === "dropoff")}>Drop-off</button>
              <button type="button" role="radio" aria-checked={kind === "inspection"} onClick={() => setKind("inspection")} className={pill(kind === "inspection")}>Inspection</button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto border-t border-zinc-100 px-5 py-3">
              {slots.isLoading ? <p className="py-8 text-center text-[13px] text-zinc-500">Loading the calendar…</p> : <SlotPicker slots={slots.data ?? []} value={picked} onChange={setPicked} days={8} />}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-zinc-100 px-5 py-3">
              <p className="text-[12.5px] text-zinc-500">{picked ? new Date(picked).toLocaleString("en-US", { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "No time picked yet"}</p>
              <button type="button" onClick={() => void book()} disabled={!picked || busy} className={cx(DARK_BTN)}>{busy ? "Booking…" : "Book"}</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
